import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * Multi-select tag chips for Lighthouse (aim / cohort) — matches lighthouse proto.
 */
export function AimTagFilter({
  options = [],
  value = [],
  onChange,
  addLabel = '+ Aim',
  emptyLabel = 'No more aims',
  ariaLabel = 'Primary aim filters',
  testId = 'onyx-aim-filter',
  itemNoun = 'aim',
  required = false,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = Array.isArray(value) ? value : [];
  const selectedSet = new Set(selected);
  const available = (options || []).filter((aim) => aim && !selectedSet.has(aim));

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  const addAim = (aim) => {
    if (!aim || selectedSet.has(aim)) return;
    onChange([...selected, aim]);
    setMenuOpen(false);
  };

  const removeAim = (aim) => {
    onChange(selected.filter((item) => item !== aim));
  };

  return (
    <div
      className={`onyx-aim-filter${menuOpen ? ' is-open' : ''}`}
      ref={rootRef}
      data-testid={testId}
      aria-label={ariaLabel}
    >
      {selected.map((aim) => (
        <span key={aim} className="onyx-aim-filter-tag">
          <span className="onyx-aim-filter-tag-label">{aim}</span>
          <button
            type="button"
            className="onyx-aim-filter-tag-remove"
            aria-label={`Remove ${aim} ${itemNoun} filter`}
            onClick={() => removeAim(aim)}
          >
            ×
          </button>
        </span>
      ))}
      <div className="onyx-aim-filter-add">
        <button
          type="button"
          className="onyx-aim-filter-add-btn"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {addLabel}
          {required ? (
            <span className="onyx-note-required-dot" aria-hidden="true" />
          ) : null}
        </button>
        {menuOpen ? (
          <div className="onyx-aim-filter-menu">
            <ul className="onyx-aim-filter-options" role="listbox">
              {available.length === 0 ? (
                <li className="onyx-aim-filter-empty">{emptyLabel}</li>
              ) : (
                available.map((aim) => (
                  <li key={aim} role="option">
                    <button
                      type="button"
                      className="onyx-aim-filter-option"
                      onClick={() => addAim(aim)}
                    >
                      <span className="onyx-aim-tag">{aim}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

AimTagFilter.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  addLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
  testId: PropTypes.string,
  itemNoun: PropTypes.string,
  required: PropTypes.bool,
};
